import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Wallet, ArrowUpRight, TrendingUp, Clock,
  DollarSign, PieChart, AlertCircle
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { getAdminDashboard } from '../services/api';
import { toast } from 'sonner';

const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await getAdminDashboard();
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Mock chart data
  const depositChart = [
    { name: 'Mon', deposits: 4500, withdrawals: 2400 },
    { name: 'Tue', deposits: 3000, withdrawals: 1398 },
    { name: 'Wed', deposits: 2000, withdrawals: 9800 },
    { name: 'Thu', deposits: 2780, withdrawals: 3908 },
    { name: 'Fri', deposits: 1890, withdrawals: 4800 },
    { name: 'Sat', deposits: 2390, withdrawals: 3800 },
    { name: 'Sun', deposits: 3490, withdrawals: 4300 },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const stats = [
    { 
      label: 'Total Users', 
      value: data?.total_users || 0, 
      icon: Users, 
      color: 'from-cyan-500 to-blue-500',
      change: '+12%'
    },
    { 
      label: 'Total Deposits', 
      value: formatCurrency(data?.total_deposits), 
      icon: Wallet, 
      color: 'from-emerald-500 to-green-500',
      change: '+18%'
    },
    { 
      label: 'Total Withdrawals', 
      value: formatCurrency(data?.total_withdrawals), 
      icon: ArrowUpRight, 
      color: 'from-orange-500 to-red-500',
      change: '+8%'
    },
    { 
      label: 'Active Investments', 
      value: data?.active_investments || 0, 
      icon: TrendingUp, 
      color: 'from-purple-500 to-pink-500',
      change: '+25%'
    },
  ];

  const alerts = [
    { type: 'warning', count: data?.pending_deposits || 0, label: 'Pending Deposits', path: '/admin/deposits' },
    { type: 'warning', count: data?.pending_withdrawals || 0, label: 'Pending Withdrawals', path: '/admin/withdrawals' },
    { type: 'info', count: data?.pending_users || 0, label: 'Pending Users', path: '/admin/users' },
  ].filter(a => a.count > 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        {/* Header */}
        <div className="admin-page-header">
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
            Admin Dashboard
          </h1>
          <p className="text-slate-400">Overview of platform metrics and pending actions</p>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-4">
              {alerts.map((alert, index) => (
                <a
                  key={index}
                  href={alert.path}
                  className="flex items-center gap-3 px-4 py-3 bg-orange-500/10 border border-orange-500/20 rounded-xl hover:bg-orange-500/20 transition-colors"
                >
                  <AlertCircle className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-300">
                    <strong>{alert.count}</strong> {alert.label}
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-400 text-sm">{stat.label}</span>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-sm text-emerald-400 mt-2">{stat.change} this month</p>
            </motion.div>
          ))}
        </div>

        {/* Monthly Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="stat-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <span className="text-slate-400">Monthly Deposits</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(data?.monthly_deposits)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="stat-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <ArrowUpRight className="w-5 h-5 text-orange-400" />
              <span className="text-slate-400">Monthly Withdrawals</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(data?.monthly_withdrawals)}</p>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-6">Deposits vs Withdrawals</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={depositChart}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0F1629',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="deposits" fill="#00FF94" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="withdrawals" fill="#FF3366" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-6">Platform Growth</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={depositChart}>
                  <defs>
                    <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00F0FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0F1629',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="deposits"
                    stroke="#00F0FF"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorDeposits)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
